import { Controller } from "@hotwired/stimulus"

// Review before ConsLog (Kloepfer K8-31, 2026-10-08): the Pending review box
// on the Feedback Loop page. Shares the page's element with feedback-list, so
// its rows, the trash and the list are all in reach. Reviewers approve (the
// row moves to the open list and goes to ConsLog) or mark N/A (the row goes
// to the trash, where Restore brings it back here); an admin names the
// reviewers. Everything updates in place — no reloads.
export default class extends Controller {
  static targets = ["box", "toggle", "body", "rows", "row", "count", "empty", "names", "settings", "reviewer", "bugsSkip", "saveBtn",
    "confirm", "confirmTitle", "confirmBody", "confirmBtn"]

  get t() {
    if (!this._t) { try { this._t = JSON.parse(this.boxTarget.dataset.i18n || "{}") } catch (e) { this._t = {} } }
    return this._t
  }

  get baseUrl() { return this.element.dataset.feedbackListBaseUrlValue || "" }

  toggle() { this._setOpen(this.bodyTarget.hidden) }

  _setOpen(open) {
    this.bodyTarget.hidden = !open
    this.toggleTarget.setAttribute("aria-expanded", String(open))
    this.toggleTarget.querySelector("[data-review-chevron]")?.classList.toggle("rotate-90", open)
  }

  stop(e) { e.stopPropagation() }

  // A row that just arrived (the panel's Send, or Restore from the trash).
  place(row) {
    this.rowsTarget.querySelector(`[data-feedback-review-target="row"][data-id="${row.dataset.id}"]`)?.remove()
    this.rowsTarget.prepend(row)
    row.classList.add("bg-yellow-50"); setTimeout(() => row.classList.remove("bg-yellow-50"), 3000)
    this.boxTarget.hidden = false
    this._setOpen(true)
    this._paint()
  }

  _paint() {
    const n = this.rowsTarget.querySelectorAll('[data-feedback-review-target="row"]').length
    this.countTarget.textContent = n
    if (this.hasEmptyTarget) this.emptyTarget.hidden = n > 0
  }

  // ── "Are you sure?" first (Phelipe 2026-10-09: a click by accident) ──
  askApprove(e) { this._ask(e, "approve") }
  askDismiss(e) { this._ask(e, "dismiss") }

  _ask(e, kind) { e.stopPropagation(); this.askFor(e.currentTarget, kind) }

  // From a Pending review row, or from the details panel's top bar (feedback-detail asks on its behalf).
  askFor(btn, kind) {
    const ev = { stopPropagation() {}, currentTarget: btn }
    if (!this.hasConfirmTarget) return kind === "approve" ? this.approve(ev) : this.dismiss(ev)
    const d = this.confirmTarget.dataset
    this._pending = { kind, btn }
    this.confirmTitleTarget.textContent = d[`${kind}Title`]
    this.confirmBodyTarget.textContent = (d[`${kind}Body`] || "").replace("%{title}", btn.dataset.title || "")
    this.confirmBtnTarget.textContent = d[`${kind}Btn`]
    this.confirmBtnTarget.className = `px-3.5 py-2 rounded-lg text-white text-[13px] font-semibold cursor-pointer disabled:opacity-60 ${kind === "approve" ? "bg-green-600 hover:bg-green-700" : "bg-gray-700 hover:bg-gray-800"}`
    this.confirmTarget.classList.remove("hidden")
    this._onConfirmKey = (ev) => { if (ev.key === "Escape") this.cancelConfirm() }
    document.addEventListener("keydown", this._onConfirmKey)
    this.confirmBtnTarget.focus()
  }

  cancelConfirm() {
    if (this.hasConfirmTarget) this.confirmTarget.classList.add("hidden")
    document.removeEventListener("keydown", this._onConfirmKey)
    this._pending = null
  }

  confirmAction() {
    const pending = this._pending
    this.cancelConfirm()
    if (!pending) return
    const ev = { stopPropagation() {}, currentTarget: pending.btn }
    return pending.kind === "approve" ? this.approve(ev) : this.dismiss(ev)
  }

  // ── Approve: on to the list and to ConsLog ──
  async approve(e) {
    e.stopPropagation()
    const btn = e.currentTarget, id = btn.dataset.id
    btn.disabled = true
    const data = await this._post(`${this.baseUrl}/${id}/approve`)
    if (!data) { btn.disabled = false; return this._toast(this.t.failed, "alert") }
    this._dropRow(id)
    this._closeDrawerIfFrom(btn)
    const list = this._list()
    if (list?.rows && data.row_html) {
      const tpl = document.createElement("template")
      tpl.innerHTML = data.row_html.trim()
      const row = tpl.content.firstElementChild
      if (row) {
        list.rows.querySelector("[data-empty]")?.remove()
        const firstSent = [...list.rows.querySelectorAll('[data-feedback-list-target="row"]')].find(r => !r.dataset.draft)
        if (firstSent) list.rows.insertBefore(row, firstSent); else list.rows.appendChild(row)
        row.classList.add("bg-yellow-50"); setTimeout(() => row.classList.remove("bg-yellow-50"), 3000)
      }
      list.ctl?.filter?.()
    }
    this._toast(this.t.approved_toast)
  }

  // ── N/A: into the trash (Restore puts it back here) ──
  async dismiss(e) {
    e.stopPropagation()
    const btn = e.currentTarget, id = btn.dataset.id
    btn.disabled = true
    const data = await this._post(`${this.baseUrl}/${id}/not_applicable`)
    if (!data) { btn.disabled = false; return this._toast(this.t.failed, "alert") }
    this._dropRow(id)
    this._closeDrawerIfFrom(btn)
    const list = this._list()
    const trash = list?.el.querySelector('[data-feedback-list-target="trashList"]')
    if (trash && data.trash_html) {
      trash.insertAdjacentHTML("afterbegin", data.trash_html)
      list.el.querySelector('[data-feedback-list-target="trashEmpty"]')?.classList.add("hidden")
      const n = list.el.querySelector('[data-feedback-list-target="trashCount"]')
      if (n) n.textContent = String((parseInt(n.textContent, 10) || 0) + 1)
    }
    this._toast(this.t.dismissed_toast)
  }

  // ── Restore from the trash: back to Pending review ──
  async restore(e) {
    const btn = e.currentTarget, id = btn.dataset.id
    btn.disabled = true
    const data = await this._post(`${this.baseUrl}/${id}/restore`)
    if (!data) { btn.disabled = false; return this._toast(this.t.failed, "alert") }
    btn.closest("[data-trash-id]")?.remove()
    const list = this._list()
    if (list) {
      const n = list.el.querySelector('[data-feedback-list-target="trashCount"]')
      if (n) n.textContent = String(Math.max(0, (parseInt(n.textContent, 10) || 0) - 1))
      const trash = list.el.querySelector('[data-feedback-list-target="trashList"]')
      if (trash && !trash.querySelector("[data-trash-id]")) list.el.querySelector('[data-feedback-list-target="trashEmpty"]')?.classList.remove("hidden")
    }
    if (data.row_html) {
      const tpl = document.createElement("template")
      tpl.innerHTML = data.row_html.trim()
      const row = tpl.content.firstElementChild
      if (row) this.place(row)
    }
    this._toast(this.t.restored_toast)
  }

  _dropRow(id) {
    this.rowsTarget.querySelector(`[data-feedback-review-target="row"][data-id="${id}"]`)?.remove()
    this._paint()
  }

  // ── Who reviews (admins) ──
  openSettings() { this.settingsTarget.classList.remove("hidden") }
  closeSettings() { if (this.hasSettingsTarget) this.settingsTarget.classList.add("hidden") }

  async saveSettings() {
    const ids = this.reviewerTargets.filter(b => b.checked).map(b => b.value)
    const names = this.reviewerTargets.filter(b => b.checked).map(b => b.dataset.name)
    this.saveBtnTarget.disabled = true
    const data = await this._post(`${this.baseUrl}/save_reviewers`, { user_ids: ids, bugs_skip_review: this.hasBugsSkipTarget && this.bugsSkipTarget.checked })
    this.saveBtnTarget.disabled = false
    if (!data) return this._toast(this.t.failed, "alert")
    if (this.hasNamesTarget) this.namesTarget.textContent = names.length ? (this.t.reviewed_by || "Reviewers: %{names}").replace("%{names}", names.join(", ")) : (this.t.no_reviewers || "")
    this.closeSettings()
    this._toast(this.t.saved_toast)
  }

  // Decided from the details panel's top bar: the panel closes, it's done.
  _closeDrawerIfFrom(btn) {
    if (btn?.closest?.("[data-review-bar]")) this._detail()?.close()
  }

  _detail() {
    const el = document.querySelector('[data-controller~="feedback-detail"]')
    return el ? this.application.getControllerForElementAndIdentifier(el, "feedback-detail") : null
  }

  _list() {
    const ctl = this.application.getControllerForElementAndIdentifier(this.element, "feedback-list")
    return { el: this.element, rows: this.element.querySelector('[data-feedback-list-target="rows"]'), ctl }
  }

  async _post(url, body = {}) {
    const res = await fetch(url, { method: "POST", credentials: "same-origin", body: JSON.stringify(body),
      headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "" } }).catch(() => null)
    return res && res.ok ? await res.json().catch(() => null) : null
  }

  _toast(message, type = "notice") {
    const el = document.createElement("div")
    el.className = type === "alert" ? "flash-toast-alert" : "flash-toast-notice"
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "4000")
    el.textContent = message || ""
    document.body.prepend(el)
  }
}
